// src/pages/ChurchList.tsx
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardMedia,
  Grid,
  CircularProgress,
  Alert,
  Stack,
  CardActionArea,
  TextField,
  InputAdornment,
  Button,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import ChurchIcon from '@mui/icons-material/Church';
import PeopleIcon from '@mui/icons-material/People';
import EventIcon from '@mui/icons-material/Event';
import PhoneIcon from '@mui/icons-material/Phone';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { getAllEglises } from '../services/egliseService';
import { Eglise } from '../types/church';
import CreateChurchDialog from '../components/CreateChurchDialog';
import { useTranslation } from 'react-i18next';

const ChurchList: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [eglises, setEglises] = useState<Eglise[]>([]);
  const [filteredEglises, setFilteredEglises] = useState<Eglise[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);

  const fetchEglises = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getAllEglises();
      setEglises(data);
      setFilteredEglises(data);
    } catch (err: any) {
      console.error('Failed to fetch églises:', err);
      setError(err.response?.data?.detail || t('churchList.errorFetching'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEglises();
  }, []);

  // Filter churches when search or status filter changes
  useEffect(() => {
    let filtered = eglises;

    // Apply search filter
    if (search) {
      filtered = filtered.filter(
        (e) =>
          e.nom.toLowerCase().includes(search.toLowerCase()) ||
          (e.email && e.email.toLowerCase().includes(search.toLowerCase())) ||
          (e.address && e.address.toLowerCase().includes(search.toLowerCase()))
      );
    }

    // Apply status filter
    if (statusFilter === 'active') {
      filtered = filtered.filter((e) => e.is_active);
    } else if (statusFilter === 'inactive') {
      filtered = filtered.filter((e) => !e.is_active);
    }

    setFilteredEglises(filtered);
  }, [search, statusFilter, eglises]);

  const handleChurchClick = (egliseId: number) => {
    navigate(`/churches/${egliseId}`);
  };

  const handleCreateSuccess = () => {
    setCreateDialogOpen(false);
    fetchEglises();
  };

  // Stats
  const totalChurches = eglises.length;
  const activeChurches = eglises.filter((e) => e.is_active).length;
  const totalMembers = eglises.reduce((acc, e) => acc + (e.user_count || 0), 0);
  const totalEvents = eglises.reduce((acc, e) => acc + (e.event_count || 0), 0);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress size={48} />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box
        sx={{
          bgcolor: 'white',
          borderRadius: 1.5,
          p: 3,
          mb: 3,
          border: '1px solid #e2e8f0',
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack direction="row" alignItems="center" spacing={2}>
            <ChurchIcon sx={{ fontSize: 28, color: '#64748b' }} />
            <Box>
              <Typography variant="h5" fontWeight={600} color="#1e293b">
                {t('churchList.title')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('churchList.subtitle')}
              </Typography>
            </Box>
          </Stack>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateDialogOpen(true)}
            sx={{ borderRadius: 1.5, px: 3 }}
          >
            {t('churchList.createChurch')}
          </Button>
        </Stack>
      </Box>

      {/* Stats */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={3}>
          <Box sx={{ bgcolor: 'white', p: 2, borderRadius: 1.5, border: '1px solid #e2e8f0' }}>
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <ChurchIcon sx={{ fontSize: 20, color: '#64748b' }} />
              <Box>
                <Typography variant="h5" fontWeight={700} color="#1e293b">{totalChurches}</Typography>
                <Typography variant="caption" color="text.secondary">{t('churchList.stats.total')}</Typography>
              </Box>
            </Stack>
          </Box>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Box sx={{ bgcolor: 'white', p: 2, borderRadius: 1.5, border: '1px solid #e2e8f0' }}>
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#16a34a' }} />
              <Box>
                <Typography variant="h5" fontWeight={700} color="#1e293b">{activeChurches}</Typography>
                <Typography variant="caption" color="text.secondary">{t('churchList.stats.active')}</Typography>
              </Box>
            </Stack>
          </Box>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Box sx={{ bgcolor: 'white', p: 2, borderRadius: 1.5, border: '1px solid #e2e8f0' }}>
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <PeopleIcon sx={{ fontSize: 20, color: '#64748b' }} />
              <Box>
                <Typography variant="h5" fontWeight={700} color="#1e293b">{totalMembers}</Typography>
                <Typography variant="caption" color="text.secondary">{t('churchList.stats.members')}</Typography>
              </Box>
            </Stack>
          </Box>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Box sx={{ bgcolor: 'white', p: 2, borderRadius: 1.5, border: '1px solid #e2e8f0' }}>
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <EventIcon sx={{ fontSize: 20, color: '#64748b' }} />
              <Box>
                <Typography variant="h5" fontWeight={700} color="#1e293b">{totalEvents}</Typography>
                <Typography variant="caption" color="text.secondary">{t('churchList.stats.events')}</Typography>
              </Box>
            </Stack>
          </Box>
        </Grid>
      </Grid>

      {/* Search and Filters */}
      <Box sx={{ bgcolor: 'white', p: 2, mb: 3, border: '1px solid #e2e8f0', borderRadius: 1.5 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }} justifyContent="space-between">
          <TextField
            placeholder={t('churchList.searchPlaceholder')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
            sx={{
              width: { xs: '100%', sm: 350 },
              '& .MuiOutlinedInput-root': { borderRadius: 1.5 },
            }}
            size="small"
          />
          <ToggleButtonGroup
            value={statusFilter}
            exclusive
            onChange={(_, value) => value && setStatusFilter(value)}
            size="small"
            sx={{
              '& .MuiToggleButton-root': {
                borderRadius: 1,
                px: 2,
                '&.Mui-selected': {
                  bgcolor: 'primary.main',
                  color: 'white',
                  '&:hover': { bgcolor: 'primary.dark' },
                },
              },
            }}
          >
            <ToggleButton value="all">{t('churchList.filters.all')}</ToggleButton>
            <ToggleButton value="active">{t('churchList.filters.active')}</ToggleButton>
            <ToggleButton value="inactive">{t('churchList.filters.inactive')}</ToggleButton>
          </ToggleButtonGroup>
        </Stack>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Churches Grid */}
      <Grid container spacing={2}>
        {filteredEglises.map((eglise) => (
          <Grid item xs={12} sm={6} md={4} key={eglise.id}>
            <Card
              elevation={0}
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                border: '1px solid #e2e8f0',
                borderRadius: 1.5,
                transition: 'all 0.2s',
                '&:hover': {
                  borderColor: '#2563eb',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                },
              }}
            >
              <CardActionArea onClick={() => handleChurchClick(eglise.id)} sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}>
                {eglise.logo_url ? (
                  <CardMedia
                    component="img"
                    height="140"
                    image={eglise.logo_url}
                    alt={eglise.nom}
                    sx={{ objectFit: 'cover' }}
                  />
                ) : (
                  <Box
                    sx={{
                      height: 100,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: '#f8fafc',
                      borderBottom: '1px solid #e2e8f0',
                    }}
                  >
                    <ChurchIcon sx={{ fontSize: 40, color: '#94a3b8' }} />
                  </Box>
                )}
                <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', p: 2 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 1 }}>
                    <Typography variant="subtitle1" fontWeight={600} color="#1e293b" noWrap sx={{ flexGrow: 1 }}>
                      {eglise.nom}
                    </Typography>
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        bgcolor: eglise.is_active ? '#16a34a' : '#94a3b8',
                        ml: 1,
                        mt: 0.75,
                      }}
                    />
                  </Stack>

                  {eglise.description && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        mb: 1.5,
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        flexGrow: 1,
                        fontSize: '0.8rem',
                      }}
                    >
                      {eglise.description}
                    </Typography>
                  )}

                  <Stack direction="row" spacing={2} sx={{ mb: 1.5 }}>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <PeopleIcon sx={{ fontSize: 14, color: '#64748b' }} />
                      <Typography variant="caption" color="text.secondary">{eglise.user_count}</Typography>
                    </Stack>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <EventIcon sx={{ fontSize: 14, color: '#64748b' }} />
                      <Typography variant="caption" color="text.secondary">{eglise.event_count}</Typography>
                    </Stack>
                  </Stack>

                  {eglise.address && (
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <LocationOnIcon sx={{ fontSize: 14, color: '#94a3b8' }} />
                      <Typography variant="caption" color="text.secondary" noWrap>
                        {eglise.address}
                      </Typography>
                    </Stack>
                  )}
                  {eglise.phone && (
                    <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.5 }}>
                      <PhoneIcon sx={{ fontSize: 14, color: '#94a3b8' }} />
                      <Typography variant="caption" color="text.secondary">
                        {eglise.phone}
                      </Typography>
                    </Stack>
                  )}
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Empty State */}
      {filteredEglises.length === 0 && !loading && (
        <Box
          sx={{
            textAlign: 'center',
            py: 6,
            px: 4,
            bgcolor: 'white',
            border: '2px dashed #e2e8f0',
            borderRadius: 1.5,
          }}
        >
          <ChurchIcon sx={{ fontSize: 48, color: '#94a3b8', mb: 2 }} />
          <Typography variant="h6" fontWeight={600} color="#1e293b" gutterBottom>
            {search || statusFilter !== 'all' ? t('churchList.noResults') : t('churchList.noChurches')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {search || statusFilter !== 'all' ? t('churchList.tryDifferentFilters') : t('churchList.createFirst')}
          </Typography>
          {!search && statusFilter === 'all' && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setCreateDialogOpen(true)}
              sx={{ borderRadius: 1.5 }}
            >
              {t('churchList.createChurch')}
            </Button>
          )}
        </Box>
      )}

      {/* Create Church Dialog */}
      <CreateChurchDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onSuccess={handleCreateSuccess}
      />
    </Box>
  );
};

export default ChurchList;
